import React, { Fragment, useState, useEffect } from "react";
import {
	
	useParams,
} from "react-router-dom";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { UtilizationChartComponent } from "../../components/chart-components/UtilizationChartComponent.jsx";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { formatValue } from "../../components/Utils.js";
import ResourceTable from "../../components/ResourceTable.jsx";
import { Tooltip } from "antd";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "../../tailwind.config.js";
import { useQuery } from "@tanstack/react-query";
import { Accordion, AccordionItem as Item } from "@szhsin/react-accordion";
import { useKeycloak } from "@react-keycloak/web";
import { useFilterStoreWithMomentDates } from "../../store/filterStore.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";


const colors = resolveConfig(tailwindConfig).theme.colors;
const API_URL = process.env.REACT_APP_API_URL;

const AccordionItem = ({ header, ...rest }) => (
	<Item
		{...rest}
		header={({ state: { isEnter } }) => (
			<>
				<div className="text-text text-xl font-semibold mt-0" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
					{header}
				</div>
				
				{isEnter ? (
					<FontAwesomeIcon
						className="ml-auto transition-transform duration-200 ease-out pt-3 pl-2"
						icon={icon({ name: "chevron-down", style: "solid" })}
					/>
				) : (
					<FontAwesomeIcon
						className="ml-auto transition-transform duration-200 ease-out pt-3 pl-2"
						icon={icon({ name: "chevron-right", style: "solid" })}
					/>
				)}
			</>
		)}
		buttonProps={{
			className: ({ isEnter }) =>
				`flex w-full py-4 pr-4 text-left hover:bg-slate-100 ${
					isEnter && "bg-slate-200"
				}`,
		}}
		contentProps={{
			className: "transition-height duration-200 ease-out",
		}}
		panelProps={{ className: "p-4" }}
	/>
);

AccordionItem.propTypes = {
	header: PropTypes.string.isRequired,
};

const ResourceDetails = (props) => {
	const { id } = useParams();
	
	// Initialize state with sessionStorage for persistence
	const [resourceDetails, setResourceDetails] = useState(() => {
		const saved = sessionStorage.getItem(`resourceDetails_${id}`);
		return saved ? JSON.parse(saved) : {};
	});
	const [resourceUtilization, setResourceUtilization] = useState(() => {
		const saved = sessionStorage.getItem(`resourceUtilization_${id}`);
		return saved ? JSON.parse(saved) : [];
	});
	const [cloudCost, setCloudCost] = useState(() => {
		const saved = sessionStorage.getItem(`cloudCost_${id}`);
		return saved ? JSON.parse(saved) : [];
	});
	const [childrenList, setChildrenList] = useState(() => {
		const saved = sessionStorage.getItem(`childrenList_${id}`);
		return saved ? JSON.parse(saved) : [];
	});
	
	const { keycloak } = useKeycloak();

	// Use Zustand store for date state with moment dates
	const { startDate, endDate } = useFilterStoreWithMomentDates();

	// Persist data to sessionStorage when it changes
	useEffect(() => {
		if (resourceDetails && Object.keys(resourceDetails).length > 0) {
			sessionStorage.setItem(`resourceDetails_${id}`, JSON.stringify(resourceDetails));
		}
	}, [resourceDetails, id]);

	useEffect(() => {
		if (resourceUtilization && resourceUtilization.length > 0) {
			sessionStorage.setItem(`resourceUtilization_${id}`, JSON.stringify(resourceUtilization));
		}
	}, [resourceUtilization, id]);

	useEffect(() => {
		if (cloudCost && cloudCost.length > 0) {
			sessionStorage.setItem(`cloudCost_${id}`, JSON.stringify(cloudCost));
		}
	}, [cloudCost, id]);

	useEffect(() => {
		if (childrenList && childrenList.length > 0) {
			sessionStorage.setItem(`childrenList_${id}`, JSON.stringify(childrenList));
		}
	}, [childrenList, id]);

	function getRequestOptions(method = "GET") {
		if (keycloak.token === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + keycloak.token);

		let requestOptions = {
			method: method,
			headers: headers,
		};

		return requestOptions;
	}

	const retrieveResourceData = async (token, id) => {
		if (token === "" || !id) return;

		const response = await fetch(
			API_URL + `/get-resource?resourceID=${encodeURIComponent(id)}&startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format("YYYY-MM-DD")}`,
			getRequestOptions()
		);

		if (!response.ok) {
			throw new Error("Failed to fetch resource");
		}

		const json = await response.json();

		if (json.Resource.resource_data) {
			const jsonResourceData = JSON.parse(json.Resource.resource_data);
			const resourceDataArray = Object.keys(jsonResourceData).map((key) => {
				// Remove curly braces
				let cleanedValue = JSON.stringify(jsonResourceData[key]).replace(
					/[{}]/g,
					""
				);

				// Remove quotes around keys and values
				cleanedValue = cleanedValue
					.replace(/"([^"]+)":/g, "$1:")
					.replace(/"/g, "");

				return { attribute: key, value: cleanedValue };
			});

			const jsonTags = json.Resource.tags ? JSON.parse(json.Resource.tags) : [];

			json.Resource.resource_data = resourceDataArray;
			json.Resource.tags = jsonTags;
			json.Resource.insights = json.Resource.insights ? JSON.parse(json.Resource.insights) : [];
		}

		return json.Resource;
	};

	useQuery(
		["resourceDetails", id, startDate?.format("YYYY-MM-DD"), endDate?.format("YYYY-MM-DD")],
		() => retrieveResourceData(keycloak.token, id),
		{
			onSuccess: (data) => {
				setResourceDetails(data);
			},
			enabled: keycloak.token !== "" && id !== undefined,
			staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
			refetchOnMount: false, // Prevent refetching when component mounts if data is fresh
		}
	);

	function buildRequest() {
		let reqFilters = [
			{
				field: "resource_parent_id",
				operator: "=",
				values: [id],
			},
		];

		return {
			filters: reqFilters,
		};
	}

	const fetchChildrenList = async () => {
		if (keycloak.token === "" || !id) return null;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + keycloak.token);

		const requestOptions = {
			body: JSON.stringify(buildRequest()),
			method: "POST",
			headers: headers,
		};

		const response = await fetch(
			API_URL + "/get-resource-list",
			requestOptions
		);
		const data = await response.json();
		return data.ResourceList;
	};

	useQuery(
		["childrenList", id],
		fetchChildrenList,
		{
			enabled: keycloak.token !== "" && id !== undefined,
			onSuccess: (data) => {
				setChildrenList(processChildrenList(data));
			},
			staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
			refetchOnMount: false, // Prevent refetching when component mounts if data is fresh
		}
	);

	function processChildrenList(data) {
		// make sure the data is sorted and grouped by service_type
		const groupedAssets = [];
		let lastServiceType = "";
		let lastGroup = {};

		if (data) {
			data.forEach((asset) => {
				if (asset.resource_type !== lastServiceType) {
					lastServiceType = asset.resource_type;
					lastGroup = {
						resource_type: asset.resource_type,
						units: 0,
						subRows: [],
					};
					groupedAssets.push(lastGroup);
				}

				lastGroup.units += asset.units;
				lastGroup.subRows.push(asset);
			});
		}

		return groupedAssets;
	}

	function buildAnalyzerRequest() {
		let reqFilters = [];
		let reqMeasures = [];
		let reqGrouping = [];
		reqFilters.push({
			field: "resource_id",
			operator: "=",
			values: [id],
		});

		reqMeasures.push({
			name: "measure",
			formula: "sum(field.direct_cost)",
		});

		reqGrouping.push("date_actual");

		return {
			measures: reqMeasures,
			filters: reqFilters,
			grouping: reqGrouping,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
			group_by_date: true,
			date_granularity: "day",
		};
	}

	const retrieveCloudCost = async (token, startDate, endDate) => {
		if (token === "" || startDate === null || endDate === null) return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token);

		let method = "POST";

		const requestBody = buildAnalyzerRequest();

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		const response = await fetch(API_URL + "/get-cloud-cost", requestOptions);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	};

	useQuery(
		["cloudCost", id, startDate?.format("YYYY-MM-DD"), endDate?.format("YYYY-MM-DD")],
		() => retrieveCloudCost(keycloak.token, startDate, endDate),
		{
			onSuccess: (data) => {
				if (data.CloudCost && data.CloudCost.details) {
					let tmpData = [];
					data.CloudCost.details.forEach((d) => {
						tmpData.push({
							start_time: d.groupings[0].value + "T00:00:00Z",
							end_time: d.groupings[0].value + "T23:59:59Z",
							cost: d.measures[0].value,
						});
					});

					setCloudCost(tmpData);
				}
			},
			enabled: keycloak.token !== "" && startDate !== null && endDate !== null,
			staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
			refetchOnMount: false, // Prevent refetching when component mounts if data is fresh
		}
	);

	const fetchResourceUtilizationData = async () => {
		if (keycloak.token === "") return null;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + keycloak.token);

		const requestBody = {
			resource_id: id,
			start_date: startDate.format("YYYY-MM-DD"),
			end_date: endDate.format("YYYY-MM-DD"),
			max_points: 150,
		};

		const requestOptions = {
			body: JSON.stringify(requestBody),
			method: "POST",
			headers: headers,
		};

		const response = await fetch(
			API_URL + "/get-resource-utilization",
			requestOptions
		);
		const data = await response.json();
		return data.ResourceUtilization;
	};

	const { isLoading } = useQuery(
		["resourceUtilization", id, startDate?.format("YYYY-MM-DD"), endDate?.format("YYYY-MM-DD")],
		fetchResourceUtilizationData,
		{
			enabled: keycloak.token !== "",
			onSuccess: (data) => {
				setResourceUtilization(data);
			},
			staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
			refetchOnWindowFocus: false, // Prevent refetching when window regains focus
			cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
			refetchOnMount: false, // Prevent refetching when component mounts if data is fresh
		}
	);



	// Check if we have any data loaded or if we're still loading
	const isDataLoaded = resourceDetails && Object.keys(resourceDetails).length > 0;
	const isAnyQueryLoading = isLoading;

	return (
		<Fragment>
			<AdministrationHeader
				title="Resources"
				className="text-text-3"
				icon={icon({ name: "layer-group", style: "light" })}
			/>

			{(() => {
				if (isAnyQueryLoading && !isDataLoaded) {
					return (
						<div className="flex justify-center items-center h-64">
							<div className="text-text-2">Loading resource details...</div>
						</div>
					);
				}
				return null;
			})()}

			{isDataLoaded && (
			<div className="text-left flex flex-auto flex-col ml-8 border-solid border-border">
				<div className="py-2 my-2 rounded-lg flex flex-col">

					<div className="bg-card rounded-card border border-card-bd p-4 mr-6">

						{/* Resource name, type and status */}
						<div className="flex flex-row">
							<div>
								<div className="font-light text-xs text-text-2">
									{resourceDetails.resource_type}
								</div>
								<div className="flex flex-row items-center mb-2">
									<div className="text-xl text-text font-semibold">
										{resourceDetails.resource_name}
									</div>
									<div>
										{(() => {
											const status = resourceDetails.resource_status;
											let statusClasses = "bg-surface-2 text-text border border-border";

											if (status && status.toLowerCase() === "active") {
												statusClasses = "bg-success-soft text-success border border-success";
											} else if (status && status.toLowerCase() === "inactive") {
												statusClasses = "bg-danger-soft text-danger border border-danger";
											}
											
											return (
												<span
													className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ml-4 ${statusClasses}`}
												>
													{status}
												</span>
											);
										})()}
									</div>
								</div>
							</div>
						</div>

						{/* Cost */}
						<div className="grid grid-cols-2 gap-x-8 mr-4 mt-4 mb-2 cursor-default">
							<div className="flex flex-row">
								<div className="flex flex-col mr-32">
									<div className="font-light text-xs text-text-2">Cost</div>
									<div className="text-base text-text font-semibold mb-2">
										<div>
											{formatValue(resourceDetails.resource_cost, "currency", false, 2)}
										</div>
									</div>
								</div>
								<div className="flex flex-col">
									<div className="font-light text-xs text-text-2">Organisation</div>
									<div className="text-base text-text font-semibold mb-2">
										{resourceDetails.organisation_name}
									</div>
								</div>
							</div>							
						</div>

						{/* Insights */}
						<div className="mr-4 mt-4 mb-8 cursor-default">
							{resourceDetails.insights && resourceDetails.insights.length > 0 ? (
								<div className="overflow-hidden border border-dashed border-warning rounded-card bg-warning-soft p-4">
									<div className="flex flex-row items-center mb-2">
										<FontAwesomeIcon
											icon={icon({
												name: "lightbulb",
												family: "sharp",
												style: "solid",
											})}
											className="text-warning mr-4 text-lg"
										/>

										<div className="flex flex-col">
											<div className="text-lg text-text font-semibold">
												Insight
											</div>
											<div className="flex flex-col text-text-2 text-base">
											{resourceDetails.insights.map((insight, idx) => (
												<div key={`insight-${idx}-${insight.insight?.slice(0, 20)}`}>
													{insight.insight}
													Potential monthly savings <span className="font-semibold text-text">{formatValue(insight.costs, "currency", false, 2)}</span>
												</div>
											))}
											</div>
										</div>
									</div>
								</div>
							) : (
								<div className="text-text-2 text-sm">No insights found</div>
							)}
						</div>

						{/* Tags */}
						<div className="mt-4 mb-2 flex flex-wrap gap-2 cursor-default">
							{resourceDetails.tags &&
								resourceDetails.tags.map((tag, index) => (
									<div
										key={`tag-${index}-${tag.key}-${tag.value}`}
										className="bg-primary-soft flex items-center px-4 text-text rounded-ctrl mr-1 text-right text-sm min-h-9"
									>
										{tag.key}:{tag.value}
									</div>
								))}
							{resourceDetails.tags && resourceDetails.tags.length === 0 && (
								<div
									key={1}
									className="bg-danger-soft flex items-center px-4 text-text rounded-ctrl mr-1 text-right text-sm min-h-9"
								>
									No tags found
								</div>
							)}
						</div>
					</div>

					{/* <div className="mb-4 mt-4">
						<div className="font-light">Parent resource</div>
						{resourceDetails.parent_resource_id > -1 && <div 
								className="text-base text-text font-semibold mb-2 cursor-pointer" 
								onClick={(e) => { gotoResource(resourceDetails.parent_resource_id)} }
							>
								{resourceDetails.parent_resource_type} {resourceDetails.parent_resource_name}
							</div>
						}
						{resourceDetails.parent_resource_id == -1 && <div 
								className="text-base text-text font-semibold mb-2 cursor-default" 
							>
								None
							</div>
						}
					</div> */}

					{/* Cost chart */}
					<div className="flex flex-row">
						{cloudCost && cloudCost.length > 0 && (
							<UtilizationChartComponent
								data={cloudCost}
								className="bg-card mb-2 rounded-card h-[300px] w-[450px] mr-4"
								title="Cost"
								// titleIcon={icons.cost}
								valueName="Cost"
								measure="cost"
								format="currency"
								date="metric_start_time"
								color="green"
								endDate={endDate}
								// min={resourceUtilization.min_max.min_storage_utilization}
								// max={resourceUtilization.min_max.max_storage_utilization}
							/>
						)}
					</div>

					{/* Utilization charts */}
					{(resourceUtilization && resourceUtilization.segments) && (resourceDetails.has_storage_metrics || resourceDetails.has_cpu_metrics || resourceDetails.has_memory_metrics) && (
					<div className="flex flex-row mt-8 bg-card rounded-card border border-card-bd p-4 mr-6">

						{/* Storage utilization */}
						{resourceUtilization &&
							resourceUtilization.segments &&
							resourceDetails.has_storage_metrics && (
								<UtilizationChartComponent
									data={resourceUtilization.segments}
									syncID="utilization"
									className="bg-card my-2 rounded-card h-[300px] w-[450px] mr-4"
									title="Storage Utilization"
									// titleIcon={icons.cost}
									valueName="Storage Utilization"
									measure="storage_utilization"
									date="metric_start_time"
									color="blue"
									min={resourceUtilization.min_max.min_storage_utilization}
									max={resourceUtilization.min_max.max_storage_utilization}
									endDate={endDate}
								/>
							)}

							{/* CPU utilization */}
							{resourceUtilization &&
								resourceUtilization.segments &&
								resourceDetails.has_cpu_metrics && (
									<UtilizationChartComponent
										data={resourceUtilization.segments}
										syncID="utilization"
										className="bg-card my-2 rounded-card h-[300px] w-[450px] mr-4"
										title="CPU Utilization"
										// titleIcon={icons.cost}
										valueName="CPU Utilization"
										measure="cpu_utilization"
										date="metric_start_time"
										color="blue"
										min={resourceUtilization.min_max.min_cpu_utilization}
										max={resourceUtilization.min_max.max_cpu_utilization}
										endDate={endDate}
									/>
								)}

								{/* Memory utilization */}
								{resourceUtilization &&
									resourceUtilization.segments &&
									resourceDetails.has_memory_metrics && (
										<UtilizationChartComponent
											data={resourceUtilization.segments}
											syncID="utilization"
											className="bg-card my-2 rounded-card h-[300px] w-[450px] mr-4"
											title="Memory Utilization"
											// titleIcon={icons.cost}
											valueName="Memory Utilization"
											measure="memory_utilization"
											date="metric_start_time"
											color="blue"
											min={resourceUtilization.min_max.min_memory_utilization}
											max={resourceUtilization.min_max.max_memory_utilization}
											endDate={endDate}
										/>
									)}
					</div>)}

					<div className="bg-card rounded-card border border-card-bd p-4 mr-6 mt-8 mb-2">
						<Accordion>
							<AccordionItem
								header="Technical Details"
								transition="true"
								transitiontimeout={250}
								className="cursor-pointer"
							>
								<div className="grid grid-cols-2 gap-x-8 mr-4 mt-4">
									{resourceDetails.resource_data &&
										resourceDetails.resource_data.map((item, index) => (
											<div className="flex flex-row" key={`resource-data-${index}-${item.attribute}`}>
												<div className="border-b border-border flex-1 text-text-3 capitalize">
													{item.attribute}
												</div>
												<div className="border-b border-border max-w-[400px] truncate cursor-default">
													<Tooltip
														title={item.value}
														color={colors.coolgrey3}
														placement="left"
													>
														{item.value}
													</Tooltip>
												</div>
											</div>
										))}
								</div>

								<div className="flex flex-row">
									{childrenList && childrenList.length > 0 && (
										<div className="text-left flex flex-auto flex-col mt-4 border-solid border-border h-[1000px]">
											<div className="text-text text-xl font-semibold mt-4 mb-8">
												Children
											</div>

											<div className="bg-card py-2 my-2 rounded-card h-screen w-screen flex flex-auto flex-col">
												<ResourceTable data={childrenList} expandAll={true} />
											</div>
										</div>
									)}
								</div>
							</AccordionItem>
						</Accordion>
					</div>
				</div>

				{/* <KPI
                    title="Total Cost"
                    currentValue={totalCosts.currentValue}
                    previousValue={totalCosts.previousValue}
                    trendData={groupCostsByDay(cloudCost.details)}
                    format="currency"
                    showChange={true}
                /> */}

				<div className="flex">
					{/* <div className="bg-white my-2 rounded-lg h-[400px] w-[500px] flex flex-none flex-col mr-4">
                        <BarChartVerticalComponent
                            data={resourceList}
                            dataKey="service_type"
                            measure="units"
                            title="Resources"
                            // titleIcon={icons.resources}
                        />
                    </div>

                    <div className="bg-white my-2 rounded-lg h-[400px] w-[500px]">
                        <BarChartVerticalComponent
                            title="IT Changes"
                        />
                    </div> */}
				</div>

				{/* <div className="bg-white py-2 my-2 rounded-lg h-screen w-screen flex flex-auto flex-col">
                    <ProductTable 
                        data={productList}
                        retrieveProducts={retrieveProducts}
                        // grouping={grouping}
                    />
                </div> */}
			</div>
			)}
		</Fragment>
	);
};

export default ResourceDetails;
